import React, { useEffect, useRef, useState } from 'react';
import { useSize } from 'react-hook-size';
import ReactMapGL, { ViewportProps, WebMercatorViewport } from 'react-map-gl';
import { env } from '../../common/util/env';
import { filterNulls } from '../../common/util/filterNulls';
import { SearchResultItem } from '../types';
import { MapMarker } from './MapMarker';
import { MapSlider } from './SearchMap.styles';

interface Props {
  searchResults: SearchResultItem[];
  expanded: boolean;
}

interface Point {
  lat: number;
  lng: number;
}

const LARGE_INT = 10000;
const SMALL_INT = -10000;

const boundingRectangle = (points: Point[]): [Point, Point] => {
  let [minLat, minLng] = [LARGE_INT, LARGE_INT];
  let [maxLat, maxLng] = [SMALL_INT, SMALL_INT];

  points.forEach(point => {
    if (point.lat < minLat) minLat = point.lat;
    if (point.lng < minLng) minLng = point.lng;
    if (point.lat > maxLat) maxLat = point.lat;
    if (point.lng > maxLng) maxLng = point.lng;
  });

  return [
    { lat: minLat, lng: minLng },
    { lat: maxLat, lng: maxLng },
  ];
};

const resultsToPoints = (results: SearchResultItem[]): Point[] =>
  filterNulls(
    results.map(result => {
      if (result.type === 'practice' && result.address)
        return { lat: result.address.latitude, lng: result.address.longitude };
      else return null;
    }),
  );

export const SearchMap = ({ searchResults, expanded }: Props) => {
  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);
  const { width, height } = useSize(mapContainerRef);

  const [viewport, setViewport] = useState<ViewportProps>({
    width: width || 400,
    height: height || 400,
  });

  useEffect(() => {
    if (width && height) {
      const points = resultsToPoints(searchResults);
      const rectangle = boundingRectangle(points);
      const [{ lat: minLat, lng: minLng }, { lat: maxLat, lng: maxLng }] = rectangle;

      setViewport(viewport => {
        const next = new WebMercatorViewport({
          ...viewport,
          width,
          height,
        }).fitBounds(
          [
            [minLng, minLat],
            [maxLng, maxLat],
          ],
          {
            padding: 100,
          },
        );

        return next;
      });
    }
  }, [width, height, searchResults]);

  const onViewportChange = (nextViewport: ViewportProps) => setViewport(nextViewport);

  const markers = filterNulls(
    searchResults.map((searchResult, idx) => {
      if (searchResult.type === 'practice') {
        return <MapMarker practice={searchResult} key={idx} />;
      } else {
        return null;
      }
    }),
  );

  const { MAPBOX_ACCESS_KEY } = env;

  return (
    <MapSlider expanded={expanded} ref={mapContainerRef}>
      <ReactMapGL
        {...viewport}
        mapboxApiAccessToken={MAPBOX_ACCESS_KEY}
        mapStyle="mapbox://styles/mapbox/light-v8"
        width="100vw"
        height={expanded ? '600px' : '0px'}
        style={{ transition: 'height 0.25s ease-out' }}
        onViewportChange={onViewportChange}
        ref={mapRef}
      >
        {markers}
      </ReactMapGL>
    </MapSlider>
  );
};
